const STANDARD_DEFAULT_ICON = {
  success: { type: "google", name: "check_circle" },
  info: { type: "google", name: "info" },
  error: { type: "google", name: "error" }
};

// No default sound constants anymore – every notification starts with sound = null.
// Consumers must call withSound() if they need audio.

let autoId = 0;
function nextId() {
  autoId += 1;
  return `notif_${Date.now()}_${autoId}`;
}

/** Shared base: only the fields and methods valid for every notification. */
class BaseNotificationBuilder {
  constructor(type, variant) {
    this._type = type;
    this._variant = variant;
    this._message = null;
    this._sound = null;          // always null unless withSound() is called
    this._options = {};
  }

  /**
   * Sets a custom ID for the notification.
   * @param {string} id - Non-empty string.
   * @returns {this}
   */
  withId(id) {
    if (typeof id !== "string" || id.length === 0) {
      throw new Error("withId() requires a non-empty string.");
    }
    this._id = id;
    return this;
  }

  /**
   * Sets the main notification text.
   * @param {string} text - Non-empty string.
   * @param {boolean} [translate=false] - Whether the text should be translated.
   * @returns {this}
   */
  withMessage(text, translate = false, variables) {
    if (typeof text !== "string" || text.length === 0) {
      throw new Error("withMessage() requires a non-empty 'text' string.");
    }
    if (typeof translate !== "boolean") {
      throw new Error("withMessage() 'translate' must be a boolean.");
    }
    this._message = { text, translate, variables };
    return this;
  }

  /**
   * Defines a sound for the notification. Pass `null` to silence it explicitly.
   * @param {string|null} url - Sound URL, or `null` to silence.
   * @param {number} [volume=1.0] - Volume between 0.0 and 1.0.
   * @returns {this}
   */
  withSound(url, volume = 1.0) {
    if (url === null) {
      this._sound = null;
      return this;
    }
    if (typeof url !== "string" || url.length === 0) {
      throw new Error("withSound() requires a non-empty 'url' string, or null to silence.");
    }
    if (typeof volume !== "number" || volume < 0 || volume > 1) {
      throw new Error("withSound() 'volume' must be a number between 0.0 and 1.0.");
    }
    this._sound = { url, volume };
    return this;
  }
  /**
   * Attaches arbitrary options to the notification.
   * @param {Object} options - Configuration options.
   * @param {number} [options.ttl] - Time-to-live in milliseconds. Determines how long the notification stays visible before auto-dismissing. Ignored if `permanent: true`.
   * @param {boolean} [options.permanent] - If `true`, the notification will not auto-close and requires manual dismissal. Overrides `ttl`.
   * @param {*} [options.*] - Any additional custom metadata or flags supported by the notification system.
   * @returns {this}
   */
  withOptions(options) {
    if (typeof options !== "object" || options === null || Array.isArray(options)) {
      throw new Error("withOptions() requires a plain object.");
    }
    this._options = options;
    return this;
  }

  /**
   * Builds the final notification data object.
   * @returns {BaseNotification} Base notification shape (message required).
   */
  build() {
    return {
      id: this._id,
      type: this._type,
      variant: this._variant,
      message: this._message,
      sound: this._sound,
      options: this._options
    };
  }
}

/**
 * Builder for standard notifications (success / info / error).
 * Adds icon and header fields that are exclusive to this type.
 */
class StandardNotificationBuilder extends BaseNotificationBuilder {
  constructor(variant) {
    super("standard", variant);
    this._id = nextId();
    this._icon = STANDARD_DEFAULT_ICON[variant]; // still has default icon
    this._header = null;
  }

  /**
   * Sets the header (bold title) of the notification.
   * @param {string} text - Non-empty string.
   * @param {boolean} [translate=false] - Whether the header text should be translated.
   * @returns {this}
   */
  withHeader(text, translate = false, variables) {
    if (typeof text !== "string" || text.length === 0) {
      throw new Error("withHeader() requires a non-empty 'text' string.");
    }
    if (typeof translate !== "boolean") {
      throw new Error("withHeader() 'translate' must be a boolean.");
    }
    this._header = { text, translate, variables };
    return this;
  }

  /**
   * Replaces the default icon with a custom one.
   * @param {string} value - Icon URL or Google Material icon name.
   * @param {string} [type="google"] - 'url', 'lucide' or 'google'.
   * @returns {this}
   */
  withIcon(value, type = "google") {
    if (type !== "url" && type !== "google" && type !== "lucide") {
      throw new Error("withIcon() 'type' must be 'url', 'lucide or 'google'.");
    }
    if (typeof value !== "string" || value.length === 0) {
      throw new Error("withIcon() requires a non-empty icon value.");
    }
    this._icon = (type === "google" || type === "lucide") ? { type, name: value } : { type, url: value };
    return this;
  }

  /**
 * Sets a Lucide React icon by component name (kebab-case or PascalCase).
 * @param {string} name - e.g. "circle-check" or "CircleCheck"
 * @param {Object} [options]
 * @param {number} [options.size]        - Icon size in px (default 16).
 * @param {number} [options.strokeWidth] - Stroke width (default 1.5).
 * @returns {this}
 */
  withLucideIcon(name, { size, strokeWidth } = {}) {
    this.withIcon(name, 'lucide');
    if (size !== undefined) {
      if (typeof size !== 'number' || size <= 0)
        throw new Error('withLucideIcon() size must be a positive number.');
      this._icon.size = size;
    }
    if (strokeWidth !== undefined) {
      if (typeof strokeWidth !== 'number' || strokeWidth <= 0)
        throw new Error('withLucideIcon() strokeWidth must be a positive number.');
      this._icon.strokeWidth = strokeWidth;
    }
    return this;
  }

  /**
   * Sets a Google Material Symbols icon by ligature name.
   * @param {string} name - e.g. "check_circle"
   * @param {Object} [options]
   * @param {number} [options.size]               - Font size in px (default 16).
   * @param {string} [options.fontVariationSettings] - Raw font-variation-settings override.
   * @returns {this}
   */
  withGoogleIcon(name, { size, fontVariationSettings } = {}) {
    this.withIcon(name, 'google');
    if (size !== undefined) {
      if (typeof size !== 'number' || size <= 0)
        throw new Error('withGoogleIcon() size must be a positive number.');
      this._icon.size = size;
    }
    if (fontVariationSettings !== undefined) {
      if (typeof fontVariationSettings !== 'string' || fontVariationSettings.length === 0)
        throw new Error('withGoogleIcon() fontVariationSettings must be a non-empty string.');
      this._icon.fontVariationSettings = fontVariationSettings;
    }
    return this;
  }

  /**
   * Sets an image icon by URL.
   * @param {string} url - Absolute or relative image URL.
   * @returns {this}
   */
  withUrlIcon(url) {
    this.withIcon(url, 'url');
    return this;
  }

  /**
   * Builds the standard notification. Header is mandatory.
   * @returns {StandardNotification} Complete standard notification data.
   */
  build() {
    if (!this._header) {
      throw new Error(`standard/${this._variant} notification requires withHeader().`);
    }
    return { ...super.build(), icon: this._icon, header: this._header };
  }
}

/**
 * Builder for message-type notifications (new message / sparks).
 * User and conversation are required constructor arguments, no setters.
 */
class MessageNotificationBuilder extends BaseNotificationBuilder {
  /**
   * @param {string} variant - "standard" or "sparks"
   * @param {User} user - User object (validated inline).
   * @param {string|Conversation} conversation - A conversation ID string or { id } object.
   */
  constructor(variant, user, conversation) {
    super("message", variant);
    this._user = validateUser(user);
    this._conversation = validateConversation(conversation);
    this._id = `conversation-${this._conversation.id}`;
  }

  /**
   * @returns {MessageNotification} The built message notification.
   */
  build() {
    return { ...super.build(), user: this._user, conversation: this._conversation };
  }
}

/**
 * Subclass of MessageNotificationBuilder that adds a required 'amount' for Sparks.
 */
class SparksNotificationBuilder extends MessageNotificationBuilder {
  /**
   * @param {User} user
   * @param {string|Conversation} conversation
   * @param {number} amount - Positive finite number of sparks sent.
   */
  constructor(user, conversation, amount) {
    super("sparks", user, conversation);
    this._amount = validateAmount(amount);
  }

  /**
   * @returns {SparksNotification} The built sparks notification.
   */
  build() {
    return { ...super.build(), amount: this._amount };
  }
}

/* ── Validation helpers ────────────────────────────────── */

/**
 * @typedef {Object} User
 * @property {string} id - Unique user identifier.
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} urlphoto - Profile picture URL.
 * @property {boolean} is_online
 */

function validateUser(user) {
  if (!user || typeof user !== "object") throw new Error("Message notifications require a 'user' object.");
  for (const field of ["id", "firstName", "lastName", "urlphoto", "is_online"]) {
    if (!(field in user)) throw new Error(`'user' object is missing required field '${field}'.`);
  }
  return user;
}

/**
 * @typedef {Object} Conversation
 * @property {string} id - Unique conversation identifier.
 */

function validateConversation(conversation) {
  if (conversation && typeof conversation === "object" && conversation.id) return conversation;
  throw new Error("'conversation' must be a conversation ID string or { id } object.");
}

function validateAmount(amount) {
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) {
    throw new Error("Sparks notifications require a positive numeric 'amount'.");
  }
  return amount;
}

/* ── Public entry point ────────────────────────────────── */

/**
 * Static factory for creating typed notification builders.
 * This is the only class exported for frontend consumption.
 */
class NotificationBuilder {
  /** @returns {StandardNotificationBuilder} */
  static success() { return new StandardNotificationBuilder("success"); }
  /** @returns {StandardNotificationBuilder} */
  static info() { return new StandardNotificationBuilder("info"); }
  /** @returns {StandardNotificationBuilder} */
  static error() { return new StandardNotificationBuilder("error"); }
  /**
   * @param {User} user
   * @param {string|Conversation} conversation
   * @returns {MessageNotificationBuilder}
   */
  static message(user, conversation, variant = "standard") { return new MessageNotificationBuilder(variant, user, conversation); }
  /**
   * @param {User} user
   * @param {string|Conversation} conversation
   * @param {number} amount
   * @returns {SparksNotificationBuilder}
   */
  static sparks(user, conversation, amount) { return new SparksNotificationBuilder(user, conversation, amount); }
}

export default NotificationBuilder;

/*
╔══════════════════════════════════════════════════════════╗
║  TYPE DEFINITIONS (for reference, not actual TypeScript) ║
╚══════════════════════════════════════════════════════════╝

interface BaseNotification {
  id: string;
  type: "standard" | "message";
  variant: "success" | "info" | "error" | "standard" | "sparks";
  message: { text: string; translate: boolean };
  sound: { url: string; volume: number } | null;
  options: Record<string, any>;
}

interface StandardNotification extends BaseNotification {
  type: "standard";
  variant: "success" | "info" | "error";
  icon: { type: "google"; name: string } | { type: "url"; url: string };
  header: { text: string; translate: boolean };
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  urlphoto: string;
  is_online: boolean;
}

interface Conversation {
  id: string;
}

interface MessageNotification extends BaseNotification {
  type: "message";
  variant: "standard" | "sparks";
  user: User;
  conversation: Conversation;
}

interface SparksNotification extends MessageNotification {
  variant: "sparks";
  amount: number;   // positive integer/float
}

╔══════════════════════════════════════════╗
║  USAGE EXAMPLES (React / Frontend)       ║
╚══════════════════════════════════════════╝

import NotificationBuilder from './NotificationBuilder';

// 1. Success notification with all features
const successNotif = NotificationBuilder.success()
  .withId("save-success")
  .withHeader("Saved!", false)
  .withIcon("cloud_done", "google")
  .withMessage("Your changes have been saved successfully.", true)
  .withSound("https://cdn.example.com/click.mp3", 0.5)
  .withOptions({ ttl: 3000, dismissible: true })
  .build();

// 2. Info notification – minimal, no sound at all (sound will be null)
const infoNotif = NotificationBuilder.info()
  .withHeader("Heads up")
  .withMessage("Your trial expires in 3 days.", false)
  .build();

// 3. Error notification with custom icon URL
const errorNotif = NotificationBuilder.error()
  .withHeader("Upload failed", false)
  .withMessage("The file is too large. Max size: 10MB.", true)
  .withIcon("https://example.com/alert.png", "url")
  .withSound("https://cdn.example.com/error.mp3", 1.0)
  .build();

// 4. Message notification (new chat message)
const msgNotif = NotificationBuilder.message(
  { id: "u123", firstName: "jane", lastName: "doe", urlphoto: "https://…", is_online: true },
  "conv_abc"                                 // shorthand for { id: "conv_abc" }
)
  .withMessage("Hey! Are you free tonight?", false)
  .withSound("https://cdn.example.com/message-chime.mp3", 0.8)
  .withOptions({ priority: "high", conversationId: "conv_abc" })
  .build();

// 5. Sparks notification (sent by a user in a conversation)
const sparksNotif = NotificationBuilder.sparks(
  { id: "u456", firstName: "jane", lastName: "doe", urlphoto: "https://…", is_online: false },
  { id: "conv_xyz" },                       // object form
  500
)
  .withMessage("You received 500 Sparks!", true)
  .withSound(null)                          // explicitly silent
  .withOptions({ ttl: 5000 })
  .build();

console.log(successNotif);
*/